import { lazy } from 'react';

// React.lazy that survives a deploy.
//
// client/build is replaced wholesale on every deploy, so a tab opened before it
// asks for a lazy chunk by its OLD hashed name and gets a 404 — the Suspense
// boundary then shows a permanently broken card until the user thinks to
// reload. The recovery is exactly that reload, done for them: the fresh
// index.html names the new chunks and the app comes back current.
//
// One reload only, guarded through sessionStorage — if the chunk still fails
// on the freshly loaded page the cause is not staleness, and a reload loop
// would trap the user on a flashing screen. The flag is cleared on the next
// successful load so a real deploy weeks later gets its own single retry.

const FLAG = 'chunk-reload-attempted';

// Pure core, dependencies injected — exported for the colocated test.
// Returns a promise that never settles when it triggers a reload: the page is
// being torn down, and letting Suspense render a fallback into it would flash.
export function recoverChunkError(err, { storage, reload }) {
  let attempted = null;
  try { attempted = storage.getItem(FLAG); } catch { /* fall through to setItem, which decides */ }
  if (attempted) throw err;
  // The flag write must land BEFORE the reload: with storage blocked there is
  // no way to remember the attempt, and reloading anyway risks an infinite
  // loop — so the error surfaces instead.
  try { storage.setItem(FLAG, '1'); } catch { throw err; }
  reload();
  return new Promise(() => {});
}

export function clearReloadFlag(storage) {
  try { storage.removeItem(FLAG); } catch { /* nothing to clear */ }
}

export default function lazyWithReload(factory) {
  return lazy(() => factory().then(
    (mod) => { clearReloadFlag(window.sessionStorage); return mod; },
    (err) => recoverChunkError(err, {
      storage: window.sessionStorage,
      reload: () => window.location.reload(),
    }),
  ));
}
